import { apiFetch } from "./apiFetch";

export interface StorageLocation {
  bucket: string;
  key: string;
}

interface SignedURLResult {
  // Absent on sync artifacts, which are ready on creation; only a ready entry is signed.
  status?: "pending" | "ready" | "failed";
  url: string;
  expiresAt: string;
  error?: string;
}

interface SignedURLsRequest {
  locations: StorageLocation[];
  ttl?: string;
}

interface SignedURLsResponse {
  urls: SignedURLResult[];
}

interface ArtifactSignedURL {
  artifactId: string;
  url: string;
  expiresAt: string;
  error?: string;
}

interface ArtifactSignedURLsRequest {
  artifactIds: string[];
}

interface ArtifactSignedURLsResponse {
  urls: ArtifactSignedURL[];
}

class ImagesClient {
  async getSignedURLs(
    locations: StorageLocation[],
    ttl?: string,
    authToken?: string,
    projectId?: string
  ): Promise<SignedURLResult[]> {
    const requestBody: SignedURLsRequest = {
      locations,
      ...(ttl && { ttl }),
    };

    const response = await apiFetch("/content/images/signed-urls", {
      scope: "project-scoped",
      authToken,
      projectIdOverride: projectId,
      method: "POST",
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get signed URLs: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = (await response.json()) as SignedURLsResponse;
    return data.urls;
  }

  async getSignedURLsByArtifactIds(
    artifactIds: string[],
    authToken?: string,
    projectId?: string
  ): Promise<ArtifactSignedURL[]> {
    const requestBody: ArtifactSignedURLsRequest = {
      artifactIds,
    };

    const response = await apiFetch("/artifacts/signed-urls", {
      scope: "project-scoped",
      authToken,
      projectIdOverride: projectId,
      method: "POST",
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to get signed URLs for artifacts: ${response.status} ${response.statusText} - ${errorText}`
      );
    }

    const data = (await response.json()) as ArtifactSignedURLsResponse;
    return data.urls;
  }
}

export const createImagesClient = (): ImagesClient => {
  return new ImagesClient();
};
